import { ReactNode } from "react";

import type { OfferKey } from "./useOfferPurchase";

type OfferItem = {
  title: string;
  description: string;
  price: string;
  isBonus?: boolean;
};

export type OfferContent = {
  // Where the offer stands among the steps to the dashboard.
  step: string;
  eyebrow: string;
  headline: ReactNode;
  highlights: ReactNode[];
  hasVideo: boolean;
  letter: ReactNode[];
  isSigned: boolean;
  productEyebrow: string;
  productName: string;
  productDescription: string;
  items: OfferItem[];
  credit: { title: string; description: string; amount: string };
  totalValue: string;
  price: string;
  savings: string;
  acceptLabel: string;
  acceptHint: string;
  declineLabel: string;
};

type Names = {
  firstName: string | null;
  platformName: string | null;
};

const DOMAIN_SETUP: OfferItem = {
  title: "Platform Domain Concierge Setup",
  description:
    "Your platform on your brand's domain. We do the DNS for you, or walk you through it live on a call. Your pick.",
  price: "$500",
};

const WALKTHROUGH: OfferItem = {
  title: "1-on-1 Platform Walkthrough",
  description: "Private training with one of our launch strategists.",
  price: "$250",
  isBonus: true,
};

// Kyle's pages (ClickUp 86e3wqp3j); the figures are his copy, not the
// catalog's prices.
export const offerContent = (
  key: OfferKey,
  { firstName, platformName }: Names,
): OfferContent => {
  // "Acme Platform" isn't "the Acme Platform platform".
  const platform = !platformName
    ? "your platform"
    : /\bplatform$/i.test(platformName)
      ? `the ${platformName}`
      : `the ${platformName} platform`;

  return key === "autopto_upsell"
    ? {
        step: "One optional step before you go to your dashboard",
        eyebrow: "One-time offer · only shown on this page",
        headline: (
          <>
            <em>I'll pay you</em> to launch your platform this week.
          </>
        ),
        highlights: [
          <>
            Pay $999 once and get <strong>$1,500 back</strong> in account
            credit.
          </>,
          <>
            We'll have {platform} live on <u>your domain</u> with your clients
            in <strong>48 hours</strong>.
          </>,
        ],
        hasVideo: true,
        letter: [
          `Hey${firstName ? ` ${firstName}` : ""}, Kyle here. Before you press “go” on ${platform}, you’ll probably want to set up the platform on your own domain and add your clients. We’ve made both those steps as easy as possible, but it still takes time and energy.`,
          "So! I wanted to offer to do all that for you. (Plus, free Bonuses listed below). We’ll set up your domain DNS settings with you and migrate your client sites in the next 48 Hours, so you can start building recurring revenue and equity value. Just to sweeten the pot, we’ll credit your account $1,500. You’re literally getting paid to get your platform launched ASAP.",
          "And, if for any reason you’re not satisfied in the next 90 days, we’ll refund your investment no-questions-asked. Sound good?",
        ],
        isSigned: true,
        productEyebrow: "Rush launch · one-time offer",
        productName: "48 Hour Push-Button Agency Platform Launch",
        productDescription:
          "Everything you need to launch to your existing and new clients, done for you.",
        items: [
          {
            title: "Full Launch in 48 Hours",
            description: "Live and Done-For-You in Two Business Days.",
            price: "$500",
          },
          {
            title: "Turn-Key Client Migration",
            description:
              "Every client site added to your platform, with its keywords.",
            price: "$500",
          },
          DOMAIN_SETUP,
          WALKTHROUGH,
          {
            title: "Team Launch Training Sessions",
            description:
              "Your team learns everything they need to sell and run it.",
            price: "$250",
            isBonus: true,
          },
          {
            title: "Dedicated Premium Support Hotline",
            description:
              "Dedicated, priority help with anything you and your clients need. Normally only available on our Enterprise plan ($1,249/month).",
            price: "$1,249",
            isBonus: true,
          },
        ],
        credit: {
          title: "+ $1,500 Account Credit",
          description:
            "Credit applied to your account instantly. The whole price back, plus $501.",
          amount: "$1,500",
        },
        totalValue: "$4,749",
        price: "$999",
        savings: "You save $3,750 (79%)",
        acceptLabel: "Yes, launch my platform in 48 hours",
        acceptHint: "Add to my order for $999",
        declineLabel:
          "No thanks, I'll set up my domain and add my clients myself.",
      }
    : {
        step: "Last step before your dashboard",
        eyebrow: "Last offer · only shown on this page",
        headline: (
          <>
            Launch on your domain in 7 days, and <em>still get paid</em>.
          </>
        ),
        highlights: [
          <>
            Pay $299 once and get <strong>$400 back</strong> in account credit.
          </>,
          <>
            We'll have {platform} live on <u>your domain</u> in{" "}
            <strong>7 days</strong>.
          </>,
        ],
        hasVideo: false,
        letter: [
          `One last thing${firstName ? `, ${firstName}` : ""}. If you don’t have clients yet or don’t need rush delivery, I’d still like to offer to set ${platformName ?? "your platform"} up on your domain for you.`,
          `We'll get ${platform} live on your own domain in the next 7 days, and we’ll walk you through it 1-on-1 so you know your way around.`,
          "And since we're crediting your account $400, it still pays for itself plus some. Fair?",
        ],
        isSigned: false,
        productEyebrow: "Done-for-you setup · one-time offer",
        productName: "7 Day All-Systems-Go Springboard",
        productDescription:
          "Your platform set up and configured on your brand's domain.",
        items: [DOMAIN_SETUP, WALKTHROUGH],
        credit: {
          title: "+ $400 Account Credit",
          description:
            "Credit applied to your account instantly. The whole price back, plus $101.",
          amount: "$400",
        },
        totalValue: "$1,150",
        price: "$299",
        savings: "You save $851 (74%)",
        acceptLabel: "Yes, set me up in 7 days",
        acceptHint: "Add to my order for $299",
        declineLabel: "No thanks, I'll handle the domain setup myself.",
      };
};
